import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CalendarClock, Clock, Loader2, CheckCircle2, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Agendar = () => {
  const [step, setStep] = useState(1); // 1=dados, 2=escolha, 3=confirmado
  const [form, setForm] = useState({ nome: '', email: '', telefone: '' });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dias, setDias] = useState([]);
  const [selDate, setSelDate] = useState('');
  const [selTime, setSelTime] = useState('');
  const [confirmado, setConfirmado] = useState(null);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const emailValido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const telDigitos = form.telefone.replace(/\D/g, '');
  const dadosOk = form.nome.trim().length >= 2 && emailValido && telDigitos.length >= 8 && consent;

  const verHorarios = async (e) => {
    e.preventDefault();
    setError('');
    if (!dadosOk) { setError('Preencha nome, e-mail e celular válidos e aceite a Política de Privacidade.'); return; }
    setLoading(true);
    try {
      const disp = await axios.get(`${API}/agendamento/disponibilidade`);
      setDias(disp.data.dias || []);
      setSelDate(disp.data.dias?.[0]?.date || '');
      setStep(2);
    } catch (err) {
      setError('Não foi possível carregar os horários. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const agendar = async () => {
    if (!selDate || !selTime) return;
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/agendamento`, {
        nome: form.nome.trim(),
        email: form.email.trim(),
        telefone: form.telefone.trim(),
        date: selDate,
        time: selTime,
        consentimento: true,
      });
      setConfirmado(data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível concluir o agendamento. Escolha outro horário.');
      try {
        const disp = await axios.get(`${API}/agendamento/disponibilidade`);
        setDias(disp.data.dias || []);
        setSelTime('');
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  };

  const diaAtual = dias.find((d) => d.date === selDate);
  const formatData = (iso) => { const [y, m, dd] = iso.split('-'); return `${dd}/${m}/${y}`; };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-yellow-50 text-gray-900">
      <header className="bg-black border-b-4 border-yellow-400 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="agendar-back-home" className="inline-flex items-center gap-2 text-gray-300 hover:text-yellow-400 font-semibold text-sm transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Voltar ao site</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-wing-white.png" alt="WING Academy Brasil" className="h-10 sm:h-12 md:h-14 w-auto object-contain" />
          </Link>
          <span className="w-[110px] hidden sm:inline-block" aria-hidden="true"></span>
          <span className="w-5 sm:hidden" aria-hidden="true"></span>
        </div>
      </header>

      <section className="container mx-auto px-4 pt-12 pb-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-2xl mb-5 shadow-lg shadow-yellow-200">
          <CalendarClock className="w-8 h-8 text-black" />
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 mb-3 leading-tight">
          Agende seu <span className="text-yellow-500">Atendimento</span>
        </h1>
        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          Fale com a WING Academy em um atendimento gratuito de <strong className="text-yellow-600">10 minutos</strong>.
          Preencha seus dados e escolha o melhor dia e horário.
        </p>
      </section>

      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-2xl mx-auto">

          {/* STEP 1 — Dados do contato */}
          {step === 1 && (
            <form onSubmit={verHorarios} className="bg-white rounded-2xl border border-yellow-200 shadow-sm p-6 sm:p-8 space-y-4" data-testid="agendar-step-dados">
              <h2 className="text-lg font-bold text-gray-900">Seus dados</h2>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Nome <span className="text-red-500">*</span></label>
                <input type="text" value={form.nome} onChange={setField('nome')} data-testid="agendar-nome-input" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" placeholder="Seu nome completo" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">E-mail <span className="text-red-500">*</span></label>
                <input type="email" value={form.email} onChange={setField('email')} data-testid="agendar-email-input" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" placeholder="voce@email.com" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">Celular / WhatsApp <span className="text-red-500">*</span></label>
                <input type="tel" value={form.telefone} onChange={setField('telefone')} data-testid="agendar-telefone-input" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" placeholder="(11) 99999-9999" />
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="agendar-consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="agendar-consent-checkbox" className="mt-1 w-4 h-4 accent-yellow-500 flex-shrink-0" />
                <span className="text-xs text-gray-500 leading-relaxed">
                  Li e concordo com a{' '}
                  <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="text-yellow-600 hover:underline">Política de Privacidade (LGPD)</a>.
                </span>
              </label>

              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="agendar-error">{error}</div>}

              <button type="submit" disabled={loading || !dadosOk} data-testid="agendar-validar-btn" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold transition-all">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronRight className="w-5 h-5" />}
                {loading ? 'Carregando...' : 'Ver horários disponíveis'}
              </button>
            </form>
          )}

          {/* STEP 2 — Escolha de dia e horário */}
          {step === 2 && (
            <div className="bg-white rounded-2xl border border-yellow-200 shadow-sm p-6 sm:p-8" data-testid="agendar-step-escolha">
              <div className="mb-6">
                <p className="text-sm text-gray-500">Olá, {form.nome || 'tudo bem'} 👋</p>
                <h2 className="text-lg font-bold text-gray-900">Escolha o dia e o horário</h2>
              </div>

              {dias.length === 0 ? (
                <p className="text-gray-500 text-center py-8" data-testid="agendar-sem-horarios">Nenhum horário disponível no momento. Tente novamente mais tarde.</p>
              ) : (
                <>
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Dia</label>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {dias.map((d) => (
                        <button key={d.date} onClick={() => { setSelDate(d.date); setSelTime(''); }} data-testid={`agendar-dia-${d.date}`} className={`flex-shrink-0 px-4 py-2.5 rounded-xl border text-center transition-all ${selDate === d.date ? 'bg-yellow-500 border-yellow-500 text-black font-bold' : 'bg-white border-gray-200 text-gray-700 hover:border-yellow-400'}`}>
                          <div className="text-xs">{d.weekday.replace('-feira', '')}</div>
                          <div className="text-sm font-bold">{formatData(d.date).slice(0, 5)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> Horário</label>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-64 overflow-y-auto pr-1">
                      {(diaAtual?.slots || []).map((t) => (
                        <button key={t} onClick={() => setSelTime(t)} data-testid={`agendar-slot-${t}`} className={`px-2 py-2 rounded-lg border text-sm font-semibold transition-all ${selTime === t ? 'bg-black border-black text-yellow-400' : 'bg-yellow-50 border-yellow-200 text-gray-800 hover:border-yellow-400'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4" data-testid="agendar-error">{error}</div>}

                  <div className="flex items-center justify-between gap-3">
                    <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-800 font-semibold">← Voltar</button>
                    <button onClick={agendar} disabled={!selTime || loading} data-testid="agendar-confirmar-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold transition-all">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                      {loading ? 'Agendando...' : selTime ? `Confirmar ${formatData(selDate).slice(0,5)} às ${selTime}` : 'Escolha um horário'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 3 — Confirmação */}
          {step === 3 && confirmado && (
            <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-8 text-center" data-testid="agendar-confirmado">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-5">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-2">Atendimento confirmado!</h2>
              <p className="text-gray-600 mb-6">Guarde os detalhes abaixo. Cada atendimento dura cerca de {confirmado.duracao_min} minutos.</p>
              <div className="inline-block text-left bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-6">
                <div className="text-sm text-gray-500">Nome</div>
                <div className="font-bold text-gray-900 mb-3">{confirmado.nome}</div>
                <div className="text-sm text-gray-500">Data e horário</div>
                <div className="font-bold text-gray-900">{confirmado.weekday}, {formatData(confirmado.date)} às {confirmado.time}</div>
              </div>
              <div>
                <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-900 hover:bg-black text-white font-bold transition-all">
                  <ArrowLeft className="w-5 h-5" /> Voltar para a WING Academy
                </Link>
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default Agendar;
